import React, { ReactElement, useMemo } from 'react'
import { Controller, SubmitHandler, UseFormProps, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'
import { yupResolver } from '@hookform/resolvers/yup'
import { AnyObjectSchema } from 'yup'

import isAura from '../../../../../lib/isAura'
import { ChargePoint, Connector, useChargePointUpdateMutation } from '../../../../../common/redux/api/web-api'
import { isChargingConnector } from '../../../../../lib/findChargingConnector'
import {
    ICapiFormInput,
    capiChargerDefaultValues,
    useCapiFormValidationSchema,
} from '../../../../../common/hooks/useCapiFormValidation'
import { mapCapiChargepoint, mapOcppChargepoint } from '../../../../../common/utils/objectMappers'
import {
    IOcppFormInput,
    ocppChargerDefaultValues,
    useOcppFormValidationSchema,
} from '../../../../../common/hooks/useOcppFormValidation'
import { transformLBCurrentInput } from '../../../../../common/utils/load-balancer-current-input-transform'
import getInstallationPhaseMode from '../../../../../lib/charge-point/getInstallationPhaseMode'
import RowDivider from '../../../../../common/components/RowDivider'
import { getChargerPhaseList } from '../../../../../common/utils/chargePointUtils'
import useText from '../../../../../common/hooks/useText'
import { useToast } from '../../../../../common/uikit/ToastProvider'
import { Input } from '../../../../../common/uikit/Input'
import { Dropdown } from '../../../../../common/uikit/Dropdown'
import { Dialog } from '../../../../../common/uikit/Dialog'

type EditChargePointSettingsDialogProps = {
    chargePoint: ChargePoint | null
    visible: boolean
    closeModal: (shouldRefreshChargePoints?: boolean) => void
}

type EditChargePointSettingsDialogContentProps = {
    chargePoint: ChargePoint
    visible: boolean
    closeModal: (shouldRefreshChargePoints?: boolean) => void
}

const getSupportedConnectorId = (connector: Connector): 1 | 2 | null => {
    if (connector.connectorId === 1 || connector.connectorId === 2) {
        return connector.connectorId
    }

    return null
}

const formId = 'edit-charge-point-settings-form'

const removeConnectorPrefixFromOfflineCurrentError = (errorMessage?: string): string | undefined =>
    errorMessage?.replace(/^.+?: (?=Current limit \(charger offline\))/, '')

type ChargePointFormInput = IOcppFormInput | ICapiFormInput

const useEditChargePointSettingsForm = (chargePoint: ChargePoint) => {
    const ocppFormValidationSchema = useOcppFormValidationSchema()
    const capiFormValidationSchema = useCapiFormValidationSchema()
    let defaultValues: ChargePointFormInput
    let validationSchema: AnyObjectSchema

    if (chargePoint.isOcpp) {
        defaultValues = ocppChargerDefaultValues(chargePoint)
        validationSchema = ocppFormValidationSchema(chargePoint)
    } else {
        defaultValues = capiChargerDefaultValues(chargePoint)
        validationSchema = capiFormValidationSchema(chargePoint, defaultValues)
    }

    return useForm<ChargePointFormInput>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        delayError: 0,
        defaultValues,
        resolver: yupResolver(validationSchema) as unknown as UseFormProps<ChargePointFormInput>['resolver'],
    })
}

export default function EditChargePointSettingsDialog({
    chargePoint,
    visible,
    closeModal,
}: EditChargePointSettingsDialogProps): ReactElement | null {
    if (!chargePoint) {
        return null
    }

    return (
        <EditChargePointSettingsDialogContent
            key={chargePoint.id}
            chargePoint={chargePoint}
            visible={visible}
            closeModal={closeModal}
        />
    )
}

const EditChargePointSettingsDialogContent = ({
    chargePoint,
    visible,
    closeModal,
}: EditChargePointSettingsDialogContentProps): ReactElement => {
    const text = useText()
    const toast = useToast()

    const installationPhaseMode = useMemo(() => getInstallationPhaseMode(chargePoint), [chargePoint])

    const phases = getChargerPhaseList(chargePoint?.productId, chargePoint?.earthingSystem, installationPhaseMode)
    const phaseOptions = phases.map((phase: string) => ({ value: phase, label: phase }))

    const chargerFormValidation = useEditChargePointSettingsForm(chargePoint)

    const {
        handleSubmit,
        control,
        formState: { errors, isDirty, isSubmitted, isValid },
    } = chargerFormValidation

    const [updateChargePoint, { isLoading: chargePointUpdating }] = useChargePointUpdateMutation()
    const saveChargePoint: SubmitHandler<ICapiFormInput | IOcppFormInput> = async (formData) => {
        try {
            await updateChargePoint({
                chargePoint: chargePoint?.isOcpp
                    ? mapOcppChargepoint(chargePoint, formData as IOcppFormInput)
                    : mapCapiChargepoint(chargePoint, formData as ICapiFormInput),
            }).unwrap()
            toast.notifySuccess(text('msgChargerUpdated'))
            closeModal(true)
        } catch (error) {
            toast.notifyError(text('msgChargerFailedToUpdate'))
            closeModal()
        }
    }

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={text('btnCancel')} type="button" text onClick={() => closeModal()} />
            <Button
                label={text('btnSave')}
                type="submit"
                form={formId}
                disabled={!isDirty || (isSubmitted && !isValid) || chargePointUpdating}
                loading={chargePointUpdating}
            />
        </div>
    )

    return (
        <Dialog
            header={text('lblLoadBalancerSettingsChargePoint')}
            visible={visible}
            onHide={() => closeModal()}
            footer={footer}
            data-testid="edit-charge-point-settings-dialog"
        >
            <form id={formId} className="flex flex-column gap-2" noValidate onSubmit={handleSubmit(saveChargePoint)}>
                <div className="flex flex-column gap-2" key={chargePoint?.loadBalancerGroupId}>
                    <div>
                        <span className="block" data-testid={`charger-id-${chargePoint?.id}`}>
                            {text('lblId')}
                        </span>
                        {chargePoint?.id}
                    </div>
                    <div>
                        <span className="block" data-testid={`charger-name-${chargePoint?.name}`}>
                            {text('lblName')}
                        </span>
                        {chargePoint?.name}
                    </div>
                </div>

                {chargePoint?.loadBalancerGroupId &&
                    chargePoint?.connectors
                        ?.filter((connector) => isChargingConnector(connector))
                        ?.map((connector) => {
                            const connectorId = getSupportedConnectorId(connector)

                            if (!connectorId) {
                                return null
                            }

                            const offlineCurrentFieldName = `lbc_${connectorId}` as const
                            const offlinePhaseFieldName = `lbOfflinePhase_${connectorId}` as const

                            return (
                                <span key={connectorId}>
                                    {isAura(chargePoint) && (
                                        <div className="mt-2">
                                            {connectorId === 1 ? (
                                                text('lblLeftSocket')
                                            ) : (
                                                <>
                                                    <RowDivider />
                                                    {text('lblRightSocket')}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <div key={connectorId} className="grid mt-2">
                                        <div className="col-12 lg:col-6">
                                            <Controller
                                                name={offlineCurrentFieldName}
                                                control={control}
                                                render={({ field: { onChange, value } }) => (
                                                    <Input
                                                        type="number"
                                                        required
                                                        label={text('lblLoadBalancerOfflineCurrentLimitField')}
                                                        infoText={text('msgLoadBalancerOfflineCurrentLimitField')}
                                                        id={offlineCurrentFieldName}
                                                        data-testid={'lbOfflineCurrent-input-' + connectorId}
                                                        onChange={(e) => {
                                                            const transformedValue = parseInt(e.target.value, 10)
                                                            if (Number.isNaN(transformedValue)) {
                                                                onChange('')
                                                                return
                                                            }
                                                            onChange(transformLBCurrentInput.input(transformedValue))
                                                        }}
                                                        error={removeConnectorPrefixFromOfflineCurrentError(
                                                            errors[offlineCurrentFieldName]?.message
                                                        )}
                                                        value={
                                                            typeof value === 'number' || typeof value === 'string'
                                                                ? value
                                                                : ''
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <div className="col-12 lg:col-6">
                                            <Controller
                                                name={offlinePhaseFieldName}
                                                control={control}
                                                render={({ field: { value, onChange, onBlur } }) => (
                                                    <Dropdown
                                                        required
                                                        showClear={false}
                                                        label={text('lblLoadBalancerOfflinePhase')}
                                                        infoText={text('msgLoadBalancerOfflinePhase')}
                                                        data-testid={`installationPhase_${connectorId}`}
                                                        id={`installationPhase_${connectorId}`}
                                                        options={phaseOptions}
                                                        value={typeof value === 'string' ? value : undefined}
                                                        onBlur={onBlur}
                                                        onChange={(event) => {
                                                            onChange(event.value)
                                                        }}
                                                        error={errors[offlinePhaseFieldName]?.message}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </span>
                            )
                        })}
            </form>
        </Dialog>
    )
}
